import { useState, useEffect } from 'react';
import { Plus, Sparkles, Trash2, ArrowUp, ArrowDown, Minus, MapPin } from 'lucide-react';
import { keywordsApi, Keyword } from '../lib/api';
import { useSelectedBusinessId } from '../hooks/useSelectedBusiness';
import {
  PageHeader,
  Card,
  Button,
  Input,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui';

const CLUSTER_COLORS: Record<string, string> = {
  гео: 'bg-blue-100 text-blue-700',
  услуги: 'bg-green-100 text-green-700',
  коммерческие: 'bg-purple-100 text-purple-700',
  информационные: 'bg-yellow-100 text-yellow-700',
  брендовые: 'bg-pink-100 text-pink-700',
};

function DeltaBadge({ delta }: { delta?: number | null }) {
  if (delta == null) return <span className="text-gray-300">—</span>;
  // Позиция: меньше = лучше. delta < 0 значит позиция улучшилась
  if (delta < 0)
    return (
      <span className="flex items-center gap-0.5 text-green-600 text-sm font-medium">
        <ArrowUp className="w-3 h-3" />
        {Math.abs(delta)}
      </span>
    );
  if (delta > 0)
    return (
      <span className="flex items-center gap-0.5 text-red-500 text-sm font-medium">
        <ArrowDown className="w-3 h-3" />
        {delta}
      </span>
    );
  return <Minus className="w-3 h-3 text-gray-400" />;
}

export function Keywords() {
  const selectedBusinessId = useSelectedBusinessId() ?? '';
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Manual add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newCluster, setNewCluster] = useState('');
  const [newFreq, setNewFreq] = useState('');
  const [bulkKeywordsInput, setBulkKeywordsInput] = useState('');
  const [isBulkAdding, setIsBulkAdding] = useState(false);

  // Inline position editing
  const [editPositions, setEditPositions] = useState<Record<string, { cur: string; prev: string }>>({});
  const [bulkCurrentPositionsInput, setBulkCurrentPositionsInput] = useState('');
  const [bulkPreviousPositionsInput, setBulkPreviousPositionsInput] = useState('');
  const [savingPositions, setSavingPositions] = useState(false);

  useEffect(() => {
    if (!selectedBusinessId) return;
    void loadKeywords();
  }, [selectedBusinessId]);

  const loadKeywords = async () => {
    setIsLoading(true);
    try {
      const res = await keywordsApi.getByBusiness(selectedBusinessId);
      setKeywords(res.data.data);
      // Init edit positions state
      const positions: Record<string, { cur: string; prev: string }> = {};
      res.data.data.forEach((k) => {
        positions[k.id] = {
          cur: k.currentPosition?.toString() ?? '',
          prev: k.previousPosition?.toString() ?? '',
        };
      });
      setEditPositions(positions);
    } catch {
      setError('Не удалось загрузить ключевые слова');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError('');
    try {
      await keywordsApi.generate(selectedBusinessId);
      await loadKeywords();
    } catch {
      setError('Ошибка генерации ключевых слов');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddManual = async () => {
    if (!newKeyword.trim()) return;
    try {
      await keywordsApi.addManual({
        businessId: selectedBusinessId,
        keyword: newKeyword.trim(),
        cluster: newCluster.trim() || undefined,
        frequency: newFreq ? parseInt(newFreq) : undefined,
      });
      setNewKeyword('');
      setNewCluster('');
      setNewFreq('');
      setShowAddForm(false);
      await loadKeywords();
    } catch {
      setError('Ключевое слово уже существует или ошибка сервера');
    }
  };

  const MAX_TRACKED_POSITION_KEYWORDS = 15;

  const handleToggleTrackPosition = async (id: string) => {
    try {
      const res = await keywordsApi.toggleTrackPosition(id);
      setKeywords((prev) => prev.map((k) => (k.id === id ? res.data.data : k)));
    } catch (err: unknown) {
      const apiError =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setError(apiError || 'Не удалось изменить отслеживание позиции');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить ключевое слово?')) return;
    try {
      await keywordsApi.delete(id);
      setKeywords((prev) => prev.filter((k) => k.id !== id));
    } catch {
      setError('Не удалось удалить ключевое слово');
    }
  };

  const handleSavePositions = async () => {
    const updates = Object.entries(editPositions)
      .filter(([, v]) => v.cur !== '' || v.prev !== '')
      .map(([id, v]) => ({
        id,
        currentPosition: parseInt(v.cur) || 0,
        previousPosition: parseInt(v.prev) || 0,
      }));

    if (updates.length === 0) return;
    setSavingPositions(true);
    try {
      await keywordsApi.updatePositions(updates);
      await loadKeywords();
    } catch {
      setError('Не удалось сохранить позиции');
    } finally {
      setSavingPositions(false);
    }
  };

  const parseBulkValues = (value: string): string[] => value
    .split(/[\n,;\t]+/g)
    .map((item) => item.trim())
    .filter(Boolean);

  const handleAddKeywordsBulk = async () => {
    const items = parseBulkValues(bulkKeywordsInput);
    if (!selectedBusinessId || items.length === 0) {
      return;
    }

    setIsBulkAdding(true);
    setError('');
    try {
      const results = await Promise.allSettled(
        items.map((keyword) => keywordsApi.addManual({ businessId: selectedBusinessId, keyword })),
      );
      const createdCount = results.filter((result) => result.status === 'fulfilled').length;
      const skippedCount = results.length - createdCount;

      if (createdCount > 0) {
        setBulkKeywordsInput('');
        await loadKeywords();
      }

      if (skippedCount > 0) {
        setError(`Добавлено ${createdCount}, пропущено ${skippedCount} (дубликаты или ошибки).`);
      }
    } catch {
      setError('Не удалось добавить ключи списком');
    } finally {
      setIsBulkAdding(false);
    }
  };

  const applyBulkPositions = (target: 'cur' | 'prev') => {
    const source = target === 'cur' ? bulkCurrentPositionsInput : bulkPreviousPositionsInput;
    const values = parseBulkValues(source);
    if (values.length === 0 || keywords.length === 0) {
      return;
    }

    setEditPositions((prev) => {
      const next = { ...prev };
      keywords.forEach((keyword, index) => {
        const nextValue = values[index];
        if (nextValue === undefined) return;
        next[keyword.id] = {
          ...(next[keyword.id] || { cur: '', prev: '' }),
          [target]: nextValue,
        };
      });
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Семантическое ядро"
        subtitle={`Ключевые слова и их позиции в выдаче · Отслеживается в Картах: ${
          keywords.filter((k) => k.isTrackedPosition).length
        } / ${MAX_TRACKED_POSITION_KEYWORDS}`}
        actions={
          <>
            <Button
              variant="primary"
              onClick={handleGenerate}
              loading={isGenerating}
              disabled={isGenerating || !selectedBusinessId}
              icon={<Sparkles className="w-3.5 h-3.5" />}
            >
              Собрать AI-семантику
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowAddForm((p) => !p)}
              icon={<Plus className="w-3.5 h-3.5" />}
            >
              Добавить вручную
            </Button>
          </>
        }
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 ml-2">✕</button>
        </div>
      )}

      {/* Manual add form */}
      {showAddForm && (
        <Card padding="sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Добавить ключевое слово</h3>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Ключевое слово *</label>
              <Input
                type="text"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                placeholder="стоматология москва"
                className="w-60"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Кластер</label>
              <Input
                type="text"
                value={newCluster}
                onChange={(e) => setNewCluster(e.target.value)}
                placeholder="гео"
                className="w-32"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Частотность</label>
              <Input
                type="number"
                value={newFreq}
                onChange={(e) => setNewFreq(e.target.value)}
                placeholder="5000"
                className="w-28"
              />
            </div>
            <Button
              variant="primary"
              onClick={handleAddManual}
              disabled={!newKeyword.trim()}
            >
              Добавить
            </Button>
          </div>
        </Card>
      )}

      <Card padding="sm" className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Добавить ключи списком</h3>
          <p className="text-xs text-gray-500 mb-2">
            Вставьте ключевые запросы по одному на строку (или через запятую/точку с запятой).
          </p>
          <textarea
            rows={5}
            value={bulkKeywordsInput}
            onChange={(e) => setBulkKeywordsInput(e.target.value)}
            placeholder={'стоматология москва\nлечение кариеса\nбрекеты цена'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
          />
          <Button
            variant="primary"
            onClick={handleAddKeywordsBulk}
            loading={isBulkAdding}
            disabled={isBulkAdding || !bulkKeywordsInput.trim()}
            className="mt-2"
          >
            {isBulkAdding ? 'Добавляем...' : 'Добавить ключи списком'}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs text-gray-600 mb-2">
              Вставьте текущие позиции списком. Значения проставятся по порядку строк в таблице.
            </p>
            <textarea
              rows={4}
              value={bulkCurrentPositionsInput}
              onChange={(e) => setBulkCurrentPositionsInput(e.target.value)}
              placeholder={'1\n2\n5\n8'}
              className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            />
            <Button
              variant="link"
              onClick={() => applyBulkPositions('cur')}
              className="mt-2 underline"
            >
              Заполнить текущие позиции
            </Button>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs text-gray-600 mb-2">
              Вставьте предыдущие позиции списком. Значения проставятся по порядку строк в таблице.
            </p>
            <textarea
              rows={4}
              value={bulkPreviousPositionsInput}
              onChange={(e) => setBulkPreviousPositionsInput(e.target.value)}
              placeholder={'3\n4\n6\n10'}
              className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            />
            <Button
              variant="link"
              onClick={() => applyBulkPositions('prev')}
              className="mt-2 underline"
            >
              Заполнить предыдущие позиции
            </Button>
          </div>
        </div>
      </Card>

      {/* Positions save button */}
      {keywords.length > 0 && (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            onClick={handleSavePositions}
            loading={savingPositions}
            disabled={savingPositions}
          >
            Сохранить позиции
          </Button>
        </div>
      )}

      {/* Keywords table */}
      {isLoading ? (
        <Card>
          <div className="text-center py-10 text-gray-400 text-sm">Загрузка...</div>
        </Card>
      ) : keywords.length === 0 ? (
        <Card>
          <div className="text-center py-14">
            <Sparkles className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">
              Нет ключевых слов. Нажмите «Собрать AI-семантику» или добавьте вручную.
            </p>
          </div>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ключевое слово</TableHead>
              <TableHead>Кластер</TableHead>
              <TableHead className="text-right">Частотность</TableHead>
              <TableHead className="text-center">Позиция</TableHead>
              <TableHead className="text-center">Пред. позиция</TableHead>
              <TableHead className="text-center">Динамика</TableHead>
              <TableHead className="text-center">Позиция в Картах</TableHead>
              <TableHead><span className="sr-only">Действия</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keywords.map((kw) => (
              <TableRow key={kw.id} className="hover:bg-gray-50">
                <TableCell>
                  <span className="font-medium text-gray-900">{kw.keyword}</span>
                  {kw.isManual && (
                    <span className="ml-2 text-xs text-gray-400">ручной</span>
                  )}
                </TableCell>
                <TableCell>
                  {kw.cluster ? (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                        CLUSTER_COLORS[kw.cluster] || 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {kw.cluster}
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-gray-600">
                  {kw.frequency?.toLocaleString() ?? '—'}
                </TableCell>
                <TableCell className="text-center">
                  <input
                    type="number"
                    value={editPositions[kw.id]?.cur ?? ''}
                    onChange={(e) =>
                      setEditPositions((prev) => ({
                        ...prev,
                        [kw.id]: { ...prev[kw.id], cur: e.target.value },
                      }))
                    }
                    placeholder="—"
                    min={1}
                    className="w-16 text-center border border-gray-200 rounded px-1.5 py-1 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </TableCell>
                <TableCell className="text-center">
                  <input
                    type="number"
                    value={editPositions[kw.id]?.prev ?? ''}
                    onChange={(e) =>
                      setEditPositions((prev) => ({
                        ...prev,
                        [kw.id]: { ...prev[kw.id], prev: e.target.value },
                      }))
                    }
                    placeholder="—"
                    min={1}
                    className="w-16 text-center border border-gray-200 rounded px-1.5 py-1 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex justify-center">
                    <DeltaBadge delta={kw.positionDelta} />
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <button
                    onClick={() => handleToggleTrackPosition(kw.id)}
                    title={
                      kw.isTrackedPosition
                        ? 'Не отслеживать позицию в Я.Картах'
                        : 'Отслеживать позицию в Я.Картах'
                    }
                    className={`p-1.5 rounded-lg transition-colors ${
                      kw.isTrackedPosition
                        ? 'text-primary-600 bg-primary-50 hover:bg-primary-100'
                        : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <MapPin className="w-3.5 h-3.5" />
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <button
                    onClick={() => handleDelete(kw.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
